import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent,
  VariantUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { sectionRenderer } from '@theme/section-renderer';

// Mirrors frontend/js/helpers/appProxyBase.ts; this asset is loaded straight
// from the import map, so it cannot import the TypeScript helper.
function appProxyBase() {
  try {
    const parsed = JSON.parse(document.body?.dataset.accountPaths || '{}');
    return typeof parsed.base === 'string' && /^\/apps\/[a-z0-9-]+$/.test(parsed.base) ? parsed.base : '/apps/shp';
  } catch {
    return '/apps/shp';
  }
}

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const requiresAge = this.getAttribute('data-requires-age-verification') === 'true';

    if (requiresAge && !window.ageVerificationService) {
      return;
    }
    this.animateAddToCart();
  }

  /**
   * @param {Event} e
   */
  scrollToRecommendations(e) {
    e.preventDefault();
    const recommendationsSection = document.querySelector('.constructor-recommendations-container');
    if (!recommendationsSection) return;
    recommendationsSection.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {string | undefined} */
  #persistentErrorMessage;

  /** @type {number} */
  #persistentErrorExpiresAt = 0;

  /** @type {string | null} */
  _optionGroupId = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }
  async addInstantUpsellToNextAutoship() {
    // Check if the add to cart button is disabled and do an early return if it is
    if (
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true'
    )
      return;

    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    const variantId = formData.get('id');
    if (!variantId) throw new Error('Variant ID is required');

    const quantity = formData.get('quantity') || 1;

    try {
      const response = await fetch(`${appProxyBase()}/customer/instant-upsell`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variant_id: variantId,
          quantity,
        }),
      });
      const data = await response.json();

      if (data.success) {
        // Add aria-live region to inform screen readers that the item was added
        if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
          const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
          const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

          this.#setLiveRegionText(addedText);

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, 5000);
        }
      }
    } finally {
    }
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this._optionGroupId === 'ship_with_next_autoship') {
      this.addInstantUpsellToNextAutoship();
      return;
    }

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#clearPersistentError();

    // Check if the add to cart button is disabled and do an early return if it is
    if (
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true'
    )
      return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);
    const formDataObject = Object.fromEntries(formData.entries());
    /** @type {Record<string, string>} */
    const properties = {};
    for (const key in formDataObject) {
      if (typeof formDataObject[key] !== 'string') continue;

      if (key.startsWith('properties[')) {
        const propertyKey = key.slice(11, -1);
        properties[propertyKey] = formDataObject[key];
      }
    }
    const originalItem = {
      id: formDataObject.id,
      quantity: formDataObject.quantity || 1,
      properties,
      selling_plan: formDataObject.selling_plan || null,
      parent_id: formDataObject.parent_id || null,
    };
    const data = {
      items: [originalItem],
      sections: (formDataObject.sections || formDataObject['section-id'] || '')
        .split(',')
        .filter(Boolean),
    };

    // If there's a selling plan, add it to the properties for BOGO grouping
    if (originalItem.selling_plan) {
      properties['_selling_plan_id'] = String(originalItem.selling_plan);
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }
    data.sections = [...new Set([...data.sections, ...cartItemComponentsSectionIds])].join(',');

    const isBogo = Number(properties['_bogo']);
    if (isBogo && !isNaN(isBogo)) {
      let quantity = Number(originalItem.quantity);
      let additionalQuantity = 0;
      if (quantity && !isNaN(quantity)) {
        additionalQuantity = quantity;
        const quantityInCart = Number(this.dataset.quantityInCart) || 0;
        const totalQuantity = quantity + additionalQuantity + quantityInCart;

        if (totalQuantity % 2 > 0) {
          // If the quantity is less than the max allowed and is an odd number, we need to add one more item
          additionalQuantity += 1;
        }

        // if item is a subscription, do not modify quantity, add second line item instead
        if (originalItem.selling_plan) {
          const secondLineItem = {
            ...originalItem,
            selling_plan: null,
            quantity: additionalQuantity,
          };
          data.items.push(secondLineItem);
        } else {
          originalItem.quantity = quantity + additionalQuantity;
        }
      }
    }

    const fetchCfg = fetchConfig('json', { body: JSON.stringify(data) });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          this.#displayErrorMessage(response.message);

          const errorDuration = 10000;
          const errorStartTime = Date.now();
          this.#persistentErrorMessage = response.message;
          this.#persistentErrorExpiresAt = errorStartTime + errorDuration;

          this.#timeout = setTimeout(() => {
            const { addToCartTextError } = this.refs;
            if (addToCartTextError) addToCartTextError.style.display = 'none';
            this.#clearLiveRegionText();
            this.#clearPersistentError();
          }, errorDuration);

          // Note: We don't dispatch CartAddEvent here because it would corrupt the cart count
          // with the requested quantity instead of the actually added quantity. The error
          // is already handled by CartErrorEvent above.
          //
          // However, if this is a maximum quantity error, the backend might have actually
          // added the maximum allowed amount despite the error. In this case, we need to
          // refresh the cart state to reflect the actual cart contents.
          // Only refresh cart if the backend actually added items despite the error
          if (
            response.message &&
            ((response.message.includes('Only') && response.message.includes('were added')) ||
              response.message.includes('due to availability'))
          ) {
            const sectionUpdatePromise = this.#updateCurrentSection();
            Promise.resolve(sectionUpdatePromise).then(() => {
              this.#restorePersistentError();
            });
            return this.#refreshCartState(originalItem.id);
          }

          return;
        } else {
          const quantity = data.items.reduce((sum, item) => {
            const itemQuantity = Number(item.quantity);
            return sum + (isNaN(itemQuantity) ? 0 : itemQuantity);
          }, 0);
          const quantityInCart = Number(this.dataset.quantityInCart) || 0;

          if (quantity && !isNaN(quantity) && !isNaN(quantityInCart)) {
            this.dataset.quantityInCart = (quantity + quantityInCart).toString();
          }

          const id = originalItem.id;

          if (addToCartTextError) {
            addToCartTextError.style.display = 'none';
            addToCartTextError.removeAttribute('aria-live');
          }

          this.#clearPersistentError();

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 10000);
          }

          this.#updateCurrentSection();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: quantity || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              variantId: id.toString(),
              sku: response.items[0].sku || null,
              psku: this.dataset.psku || null,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  #updateCurrentSection() {
    const currentSection = this.closest('[data-section-id]');
    if (
      currentSection &&
      currentSection instanceof HTMLElement &&
      currentSection.dataset.sectionId
    ) {
      return sectionRenderer.renderSection(currentSection.dataset.sectionId, { cache: false });
    }
  }

  /**
   * Displays an error message in the add to cart error element
   * @param {string} message - The error message to display
   */
  #displayErrorMessage(message) {
    const { addToCartTextError } = this.refs;
    if (!addToCartTextError) return;

    addToCartTextError.style.display = '';
    const textNode = addToCartTextError.querySelector('span');
    if (textNode) {
      textNode.textContent = message;
    } else {
      const newTextNode = document.createElement('span');
      newTextNode.textContent = message;
      addToCartTextError.appendChild(newTextNode);
    }
    this.#setLiveRegionText(message);
  }

  /**
   * Clears the persistent error state
   */
  #clearPersistentError() {
    this.#persistentErrorMessage = undefined;
    this.#persistentErrorExpiresAt = 0;
  }

  /**
   * Restores the persistent error message after a section update if it hasn't expired
   */
  #restorePersistentError() {
    if (!this.#persistentErrorMessage) return;

    const remaining = this.#persistentErrorExpiresAt - Date.now();
    if (remaining <= 0) {
      this.#clearPersistentError();
      return;
    }

    this.#displayErrorMessage(this.#persistentErrorMessage);

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      const { addToCartTextError } = this.refs;
      if (addToCartTextError) addToCartTextError.style.display = 'none';
      this.#clearLiveRegionText();
      this.#clearPersistentError();
    }, remaining);
  }

  /**
   * Refreshes the cart state by fetching the current cart data and dispatching a CartAddEvent.
   * This should be used when the backend might have added items despite returning an error.
   */
  async #refreshCartState(variantId) {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);

      if (!response.ok) {
        return;
      }

      const cartData = await response.json();
      const itemCount = cartData.item_count || 0;

      const item = cartData.items.find((item) => item.variant_id == variantId);
      const sku = item ? item.sku : null;
      const previousQuantity = Number(this.dataset.quantityInCart) || 0;
      const addedQuantity = item ? Math.max(0, item.quantity - previousQuantity) : 0;

      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          itemCount,
          source: 'product-form-component-refresh',
          productId: this.dataset.productId,
          variantId: variantId?.toString(),
          sku,
          addedQuantity,
          sections: {},
        })
      );
    } catch (error) {
      console.error('Error refreshing cart state:', error);
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    console.log('Variant update event received in product form component', event.detail.data);
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource === null || event.detail.resource.available === false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          `${productVariantMedia}&width=100`
        );
    }
  };

  /**
   * @param {CustomEvent} e
   */
  onProductPlanChange(e) {
    if (!(e instanceof CustomEvent)) return;

    const { optionGroupId, atcText } = e.detail;

    this._optionGroupId = optionGroupId;

    const addToCartButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    const textContainer = addToCartButton?.querySelector('.add-to-cart-text__content');
    if (textContainer instanceof HTMLElement) {
      textContainer.textContent = atcText || textContainer.dataset.defaultText;
    }
    if (addToCartButton instanceof HTMLElement && atcText) {
      addToCartButton.ariaLabel = atcText;
    }
  }

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * t ** 3 + bX * t ** 2 + cX * t + p0.x;
  const y = aY * t ** 3 + bY * t ** 2 + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormPlanSelectRefs
 * @property {HTMLInputElement} sellingPlanInput - The form input for submitting the selling plan.
 * @property {HTMLElement[]} optionGroup - The option group elements.
 * @property {HTMLElement | undefined} shipWithNextAutoship - The ship with next autoship option.
 * @property {HTMLElement | undefined} nextAutoshipDate - The next autoship date element.
 *
 * @extends Component<ProductFormPlanSelectRefs>
 */
class ProductFormPlanSelect extends Component {
  requiredRefs = ['sellingPlanInput', 'optionGroup'];
  #abortController = new AbortController();

  /** @type {string | null} */
  _variantId = null;
  /** @type {string | null} */
  _nextOrderDate = null;
  /** @type {boolean} */
  _upsellEligible = false;

  static get observedAttributes() {
    return ['variant-id'];
  }

  connectedCallback() {
    super.connectedCallback();

    this._variantId = this.getAttribute('variant-id');

    this.initInstantUpsell();
    this.syncZinreloPriceOnLoad();

    const target = this.closest('.shopify-section, dialog');
    if (target) {
      const { signal } = this.#abortController;
      target.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles variant update events to update price display.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      return;
    }

    const updatedComponent = event.detail.data.html.querySelector('product-form-plan-select');
    if (!updatedComponent) return;

    const newVariantId = updatedComponent.getAttribute('variant-id');
    const variantChanged = newVariantId && newVariantId !== this._variantId;

    if (newVariantId) {
      this.setAttribute('variant-id', newVariantId);
      this._variantId = newVariantId;
    }

    if (variantChanged) {
      this.initInstantUpsell();
    }

    const currentOptionGroups = this.querySelectorAll('.product-form-plan-option-price');
    const updatedOptionGroups = updatedComponent.querySelectorAll(
      '.product-form-plan-option-price'
    );

    if (currentOptionGroups.length === updatedOptionGroups.length) {
      currentOptionGroups.forEach((currentGroup, index) => {
        const updatedGroup = updatedOptionGroups[index];
        if (updatedGroup && currentGroup.innerHTML !== updatedGroup.innerHTML) {
          morph(currentGroup, updatedGroup);
        }
      });
    }
  };

  /**
   * @param {string} name
   * @param {string | null} oldVal
   * @param {string | null} newVal
   */
  attributeChangedCallback(name, oldVal, newVal) {
    if (name === 'variant-id' && oldVal !== newVal) {
      this._variantId = newVal;
    }
  }

  async initInstantUpsell() {
    const instantUpsellGroup = this.refs.optionGroup.find(
      (el) => el instanceof HTMLElement && el.dataset.shipWithNextAutoship !== undefined
    );
    if (instantUpsellGroup) {
      instantUpsellGroup.style.display = 'none';
      await this.#fetchInstantUpsellConfig();

      if (this._upsellEligible && this._nextOrderDate) {
        if (this.refs.nextAutoshipDate) {
          const date = new Date(this._nextOrderDate);
          const formattedDate = date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
          this.refs.nextAutoshipDate.textContent = formattedDate;
        }
        instantUpsellGroup.style.display = '';
      }
    }
  }

  async #fetchInstantUpsellConfig() {
    this._nextOrderDate = null;
    this._upsellEligible = false;
    const { signal } = this.#abortController;

    const response = await fetch(
      `${appProxyBase()}/customer/instant-upsell?variant_id=${this._variantId}`,
      { signal }
    );
    const data = await response.json();

    if (data?.next_order_date) {
      this._nextOrderDate = data.next_order_date;
    }
    if (data?.upsell_eligible) {
      this._upsellEligible = data.upsell_eligible;
    }
  }

  /**
   * @param {Element | null} optionGroupEl
   * @returns {string | null}
   */
  getSelectedPlanPrice(optionGroupEl) {
    if (!optionGroupEl) return null;

    const sellingPlanSelect = optionGroupEl.querySelector('select[data-selling-plan-select]');

    if (!(sellingPlanSelect instanceof HTMLSelectElement)) return null;

    const selectedOption = sellingPlanSelect.selectedOptions[0];

    if (!(selectedOption instanceof HTMLOptionElement)) return null;

    return selectedOption.dataset.zrlPrice?.trim() ?? null;
  }

  /**
   * @returns {string | null}
   */
  getCurrentPriceValue() {
    const currentPriceEl = document.getElementById('current-price');
    if (!currentPriceEl) return null;
    return currentPriceEl.textContent?.trim() || null;
  }

  /**
   * @returns {string | null}
   */
  storeBasePrice() {
    const currentPriceEl = document.getElementById('current-price');

    if (!currentPriceEl) return null;

    if (!currentPriceEl.dataset.basePrice) {
      currentPriceEl.dataset.basePrice = currentPriceEl.textContent?.trim() || '';
    }

    return currentPriceEl.dataset.basePrice || null;
  }

  /**
   * @param {string} priceValue
   */
  setZinreloPrice(priceValue) {
    const normalizedPrice = priceValue.trim();

    if (!normalizedPrice) return;

    const currentPriceEl = document.getElementById('current-price');

    if (currentPriceEl) {
      currentPriceEl.textContent = normalizedPrice;
    }

    const ogPriceMeta = document.getElementById('og-price-amount');

    if (ogPriceMeta) {
      ogPriceMeta.setAttribute('content', normalizedPrice);
    }

    if (window.zrl_mi && typeof window.zrl_mi.replace_product_page_potential === 'function') {
      window.zrl_mi.replace_product_page_potential();
    }
  }

  restoreBasePrice() {
    const currentPriceEl = document.getElementById('current-price');
    const basePrice = currentPriceEl?.dataset.basePrice;

    if (!basePrice) return;

    this.setZinreloPrice(basePrice);
  }

  /**
   * @param {Element | null} optionGroupEl
   */
  updateZinreloForSelectedPlan(optionGroupEl) {
    const selectedPlanPrice = this.getSelectedPlanPrice(optionGroupEl);

    if (!selectedPlanPrice) return;

    this.storeBasePrice();
    this.setZinreloPrice(selectedPlanPrice);
  }

  syncZinreloPriceOnLoad() {
    const selectedInput = this.querySelector('input[data-option-group]:checked');
    const selectedOptionGroup =
      this.querySelector('[ref="optionGroup[]"].selected') ||
      (selectedInput ? selectedInput.closest('[ref="optionGroup[]"]') : null);
    const selectedPlanPrice = this.getSelectedPlanPrice(selectedOptionGroup);
    if (selectedPlanPrice) {
      this.updateZinreloForSelectedPlan(selectedOptionGroup);
      return;
    }
    const currentPrice = this.getCurrentPriceValue();
    if (!currentPrice) return;
    this.storeBasePrice();
    this.setZinreloPrice(currentPrice);
  }

  /**
   * Handles changes to the option group radio buttons.
   * @param {Event} e - The change event.
   */
  onOptionGroupChange(e) {
    if (!(e.target instanceof HTMLInputElement)) return;

    const selectedOptionGroup = e.target.closest('[ref="optionGroup[]"]');

    for (const optionGroup of this.refs.optionGroup) {
      optionGroup.classList.toggle('selected', selectedOptionGroup === optionGroup);
    }

    const optionGroupId = e.target.dataset.optionGroup || '';
    if (['one_time_purchase', 'ship_with_next_autoship'].includes(optionGroupId)) {
      this.refs.sellingPlanInput.value = '';
      this.setAttribute('data-subscription-interval', '');
      this.restoreBasePrice();
    } else {
      const sellingPlanSelect = this.querySelector('select[data-selling-plan-select]');
      if (sellingPlanSelect instanceof HTMLSelectElement) {
        this.refs.sellingPlanInput.value = sellingPlanSelect.value;
        const selectedOption = sellingPlanSelect.selectedOptions[0];
        if (selectedOption) {
          const interval = selectedOption.dataset.value || '';
          this.setAttribute('data-subscription-interval', interval);
        } else {
          this.setAttribute('data-subscription-interval', sellingPlanSelect.value);
        }
      } else {
        this.refs.sellingPlanInput.value = '';
        this.setAttribute('data-subscription-interval', '');
      }

      this.updateZinreloForSelectedPlan(selectedOptionGroup);
    }

    this.dispatchEvent(
      new CustomEvent('change', {
        detail: {
          optionGroupId,
          atcText: e.target.dataset.atcText || null,
        },
      })
    );
  }

  /**
   * Handles changes to the option group radio buttons.
   * @param {Event} e - The change event.
   */
  onSellingPlanChange(e) {
    if (!(e.target instanceof HTMLSelectElement)) return;

    const selectedOptionGroup = e.target.closest('[ref="optionGroup[]"]');
    const optionGroupRadio = selectedOptionGroup?.querySelector('input[data-option-group]');
    if (optionGroupRadio instanceof HTMLInputElement) {
      const event = new Event('change');
      optionGroupRadio.checked = true;
      optionGroupRadio.dispatchEvent(event);
    }
  }
}

if (!customElements.get('product-form-plan-select')) {
  customElements.define('product-form-plan-select', ProductFormPlanSelect);
}
